import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package, ShoppingCart, LogOut, Plus, Pencil, Trash2, X, Upload, Sparkles, Search, Star, StarOff,
  Image as ImageIcon, Layers, CheckCircle2, MessageCircle, Mail, ChevronDown, Camera, Crop as CropIcon,
  Gift, Palette, ArrowUp, ArrowDown,
} from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, LOGO_URL, BRAND } from "@/lib/brand";
import CropModal from "@/components/CropModal";
import StarLoader from "@/components/StarLoader";

const empty = { name: "", price: "", original_price: "", category: "earrings", description: "", styling_tip: "", stock: 10, featured: false, out_of_stock: false, low_stock: false, collection_ids: [], occasions: [], bundle_with: [] };

/**
 * BundleWithPicker — small searchable multi-select (max 2) for selecting companion
 * products that appear in Moon's Pairings on the product detail page.
 */
function BundleWithPicker({ allProducts, currentProductId, value, onChange }) {
  const [q, setQ] = useState("");
  const selectable = (allProducts || []).filter((p) => p.id !== currentProductId);
  const chosen = value.map((id) => selectable.find((p) => p.id === id)).filter(Boolean);
  const query = q.trim().toLowerCase();
  const suggestions = query
    ? selectable
        .filter((p) => !value.includes(p.id))
        .filter((p) => p.name.toLowerCase().includes(query) || p.category.toLowerCase().includes(query))
        .slice(0, 6)
    : [];

  const toggle = (id) => {
    if (value.includes(id)) return onChange(value.filter((x) => x !== id));
    if (value.length >= 2) {
      toast.error("Maximum 2 bundle products");
      return;
    }
    onChange([...value, id]);
    setQ("");
  };

  return (
    <div className="mt-2 space-y-3" data-testid="editor-bundle-with">
      {/* Chosen chips */}
      {chosen.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {chosen.map((p) => (
            <span key={p.id} className="inline-flex items-center gap-2 pl-1 pr-2 py-1 bg-sj-gold/15 border border-sj-gold/30 rounded-full text-xs" data-testid={`editor-bundle-chip-${p.id}`}>
              {p.images?.[0] ? (
                <img src={resolveImage(p.images[0], { w: 80 })} alt="" className="h-6 w-6 rounded-full object-cover" />
              ) : (
                <span className="h-6 w-6 rounded-full bg-sj-cream" />
              )}
              <span className="text-sj-ink font-medium">{p.name}</span>
              <button type="button" onClick={() => toggle(p.id)} className="text-sj-inkSoft hover:text-sj-rose" data-testid={`editor-bundle-remove-${p.id}`}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search + suggestions (hidden once 2 chosen) */}
      {value.length < 2 && (
        <div className="relative">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products to bundle…"
            className="w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-10 text-sm"
            data-testid="editor-bundle-search"
          />
          {suggestions.length > 0 && (
            <div className="absolute z-10 mt-1 left-0 right-0 bg-white border border-sj-ink/15 rounded-md shadow-lg max-h-72 overflow-auto" data-testid="editor-bundle-suggestions">
              {suggestions.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => toggle(p.id)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-sj-cream/60 transition-colors"
                  data-testid={`editor-bundle-suggest-${p.id}`}
                >
                  {p.images?.[0] ? (
                    <img src={resolveImage(p.images[0], { w: 80 })} alt="" className="h-8 w-8 rounded-md object-cover" />
                  ) : (
                    <span className="h-8 w-8 rounded-md bg-sj-cream" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-sj-ink truncate">{p.name}</span>
                    <span className="block text-[11px] text-sj-muted uppercase tracking-wide">{p.category} · ₹{Number(p.price).toLocaleString("en-IN")}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders]     = useState([]);
  const [collections, setCollections] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [banners, setBanners]   = useState([null, null, null, null, null]);
  const [bannerLinks, setBannerLinks] = useState([null, null, null, null, null]);
  const [mobileBanners, setMobileBanners] = useState([null, null, null, null, null]);
  const [q, setQ]               = useState("");
  const [loading, setLoading]   = useState(false);
  const [editorOpen, setEditor] = useState(false);
  const [editing, setEditing]   = useState(null);   // product being edited (null = create)
  const [form, setForm]         = useState(empty);
  const [files, setFiles]       = useState([]);     // new files to upload
  const [existingImgs, setExistingImgs] = useState([]);
  const [busy, setBusy]         = useState(false);
  const [aiBusy, setAiBusy]     = useState(false);
  const [tipBusy, setTipBusy]   = useState(false);
  // Crop-modal state — cropQueue is FIFO for newly-picked files; recropIdx
  // targets an already-added pending file (in `files`) for re-cropping.
  const [cropQueue, setCropQueue] = useState([]); // File[]
  const [recropIdx, setRecropIdx] = useState(null);
  const [fileColors, setFileColors] = useState([]); // parallel to files
  const [existingColors, setExistingColors] = useState([]); // parallel to existingImgs

  const load = async () => {
    setLoading(true);
    try {
      const [p, o, c, occ] = await Promise.all([
        api.get("/products", { params: { include_hidden: true, ...(q ? { q } : {}) } }),
        api.get("/admin/orders", { params: { limit: 50 } }),
        api.get("/collections"),
        api.get("/occasions"),
      ]);
      setProducts(p.data);
      // Handle new paginated response { orders, total, ... } OR legacy array
      const ordersData = Array.isArray(o.data) ? o.data : (o.data?.orders || []);
      setOrders(ordersData);
      setCollections(c.data);
      setOccasions(occ.data || []);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Load banners only when the Home banner tab is opened (they're only needed there)
  useEffect(() => {
    if (tab !== "banners") return;
    api.get("/banners").then(({ data }) => {
      setBanners((data.slots || []).concat([null, null, null, null, null]).slice(0, 5));
      setBannerLinks((data.links || []).concat([null, null, null, null, null]).slice(0, 5));
      setMobileBanners((data.mobile_slots || []).concat([null, null, null, null, null]).slice(0, 5));
    }).catch(() => {});
  }, [tab]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const doLogout = () => { logout(); nav("/admin/login"); };

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setFiles([]);
    setExistingImgs([]);
    setExistingColors([]);
    setFileColors([]);
    setEditor(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, price: p.price, original_price: p.original_price ?? "", category: p.category,
      description: p.description || "", styling_tip: p.styling_tip || "", stock: p.stock,
      featured: p.featured || false,
      out_of_stock: p.out_of_stock || false,
      low_stock: p.low_stock || false,
      collection_ids: p.collection_ids || [],
      occasions: p.occasions || [],
      bundle_with: p.bundle_with || [],
    });
    setExistingImgs(p.images || []);
    // Initialize existingColors parallel to images (padded to length)
    const imgs = p.images || [];
    const colors = p.image_colors || [];
    const padded = imgs.map((_, i) => (colors[i] || ""));
    setExistingColors(padded);
    setFiles([]);
    setFileColors([]);
    setEditor(true);
  };

  const closeEditor = () => { if (!busy) setEditor(false); };

  const onFiles = (e) => {
    const list = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    if (list.length === 0) return;
    setCropQueue((prev) => [...prev, ...list]);
    // Allow selecting the same file again next time
    e.target.value = "";
  };

  const onCropSaved = (croppedFile) => {
    if (recropIdx !== null) {
      setFiles((prev) => prev.map((f, i) => (i === recropIdx ? croppedFile : f)));
      setRecropIdx(null);
      return;
    }
    setFiles((prev) => [...prev, croppedFile]);
    setFileColors((prev) => [...prev, ""]);
    setCropQueue((prev) => prev.slice(1));
  };

  const onCropCancelled = () => {
    if (recropIdx !== null) { setRecropIdx(null); return; }
    setCropQueue((prev) => prev.slice(1));
  };

  const currentCropFile = recropIdx !== null
    ? (files[recropIdx] || null)
    : (cropQueue[0] || null);

  const removeNewFile = (i) => {
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
    setFileColors((prev) => prev.filter((_, idx) => idx !== i));
  };

  const removeExistingImg = async (path) => {
    if (!editing) {
      const idx = existingImgs.indexOf(path);
      setExistingImgs((prev) => prev.filter((p) => p !== path));
      if (idx >= 0) setExistingColors((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    try {
      await api.delete(`/admin/products/${editing.id}/images`, { params: { image: path } });
      const idx = existingImgs.indexOf(path);
      setExistingImgs((prev) => prev.filter((p) => p !== path));
      if (idx >= 0) setExistingColors((prev) => prev.filter((_, i) => i !== idx));
      toast.success("Image removed");
    } catch { toast.error("Failed to remove image"); }
  };

  const generateAI = async () => {
    if (!form.name || !form.category) return toast.error("Add a name and category first");
    setAiBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("category", form.category);
      // Prefer newly-picked cropped files; fall back to fetching a couple existing ones
      const fromNew = files.slice(0, 3);
      for (const f of fromNew) fd.append("files", f);
      if (fromNew.length === 0 && existingImgs.length > 0) {
        // Fetch up to 2 existing images and include them as blobs
        for (const path of existingImgs.slice(0, 2)) {
          try {
            const res = await api.get(`/images/${path}`, { responseType: "blob" });
            fd.append("files", new File([res.data], "existing.jpg", { type: res.data.type || "image/jpeg" }));
          } catch { /* skip on failure */ }
        }
      }
      const { data } = await api.post("/admin/ai/describe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Endpoint now always returns 200; check for `error` field first.
      if (data?.error || !data?.description) {
        toast.error(data?.error || "AI generation failed — try again");
        return;
      }
      setForm((f) => ({ ...f, description: data.description }));
      toast.success(data.used_vision ? "Description drafted from your photos" : "Description drafted — edit as you like");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Network hiccup — check your connection and try again.");
    } finally { setAiBusy(false); }
  };

  const generateStylingTip = async () => {
    if (!form.name || !form.category) return toast.error("Add a name and category first");
    if (files.length === 0 && existingImgs.length === 0) {
      return toast.error("Add at least one product photo so Moon can analyse it");
    }
    setTipBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("category", form.category);
      fd.append("description", form.description || "");
      const allColors = [...existingColors, ...fileColors].map((c) => (c || "").trim()).filter(Boolean);
      fd.append("colors", [...new Set(allColors)].join(", "));
      for (const f of files.slice(0, 8)) fd.append("files", f);
      fd.append("existing_paths", existingImgs.slice(0, Math.max(0, 8 - files.length)).join(","));
      const { data } = await api.post("/admin/ai/styling-tip", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Backend now returns HTTP 200 with either `{styling_tip, used_vision}` on
      // success OR `{styling_tip: null, error: "..."}` on failure. We check
      // the `error` field first so Cloudflare never has a chance to hijack
      // the response with its own 5xx error page.
      if (data?.error || !data?.styling_tip) {
        toast.error(data?.error || "Moon couldn't style this right now — try again.");
        return;
      }
      setForm((f) => ({ ...f, styling_tip: data.styling_tip }));
      toast.success("Moon styled this piece from the photo");
    } catch (e) {
      // Network-level errors only reach here (endpoint always returns 200).
      toast.error(e.response?.data?.detail || "Network hiccup — check your connection and try again.");
    } finally { setTipBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) return toast.error("Name and price are required");
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        price: Number(form.price),
        original_price: form.original_price === "" || form.original_price == null ? null : Number(form.original_price),
        category: form.category,
        description: form.description,
        styling_tip: form.styling_tip || "",
        stock: Number(form.stock || 0),
        featured: form.featured,
        out_of_stock: form.out_of_stock,
        low_stock: form.low_stock,
        collection_ids: form.collection_ids || [],
        occasions: form.occasions || [],
        bundle_with: (form.bundle_with || []).slice(0, 2),
      };
      let productId = editing?.id;
      if (!editing) {
        const { data } = await api.post("/admin/products", payload);
        productId = data.id;
      } else {
        await api.put(`/admin/products/${editing.id}`, payload);
      }
      if (files.length > 0) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        // colors sent as pipe-separated string, one entry per file (blank = untagged)
        fd.append("colors", fileColors.map((c) => (c || "").trim()).join("|"));
        await api.post(`/admin/products/${productId}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      // If editing, also PUT any updated colors for existing images
      if (editing && existingImgs.length > 0) {
        try {
          await api.put(`/admin/products/${productId}/image-colors`, {
            image_colors: existingColors.map((c) => (c && c.trim()) || null),
          });
        } catch { /* non-blocking */ }
      }
      // Sync collection membership (product.collection_ids is source of truth)
      // Rebuild each collection's product_ids from the products payload
      const desiredIds = new Set(form.collection_ids || []);
      const wasIds = new Set(editing?.collection_ids || []);
      const added = [...desiredIds].filter((id) => !wasIds.has(id));
      const removed = [...wasIds].filter((id) => !desiredIds.has(id));
      await Promise.all([
        ...added.map(async (cid) => {
          const c = collections.find((x) => x.id === cid);
          if (!c) return;
          const ids = Array.from(new Set([...(c.product_ids || []), productId]));
          await api.put(`/admin/collections/${cid}`, { product_ids: ids });
        }),
        ...removed.map(async (cid) => {
          const c = collections.find((x) => x.id === cid);
          if (!c) return;
          const ids = (c.product_ids || []).filter((pid) => pid !== productId);
          await api.put(`/admin/collections/${cid}`, { product_ids: ids });
        }),
      ]);
      toast.success(editing ? "Product updated" : "Product added");
      setEditor(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const doDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Deleted");
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
    } catch { toast.error("Delete failed"); }
  };

  const toggleFeatured = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}`, { featured: !p.featured });
      setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, featured: !p.featured } : x));
    } catch { toast.error("Update failed"); }
  };

  const markCompleted = async (order) => {
    try {
      const { data } = await api.put(`/admin/orders/${order.id}/status`, { status: "completed" });
      setOrders((prev) => prev.map((x) => x.id === order.id ? data : x));
      toast.success("Order marked as completed");
    } catch { toast.error("Update failed"); }
  };

  const reopenOrder = async (order) => {
    try {
      const { data } = await api.put(`/admin/orders/${order.id}/status`, { status: "new" });
      setOrders((prev) => prev.map((x) => x.id === order.id ? data : x));
      toast.message("Order re-opened");
    } catch { toast.error("Update failed"); }
  };

  const deleteOrder = async (order) => {
    if (!window.confirm(`Delete order ${order.id.slice(0, 8)}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${order.id}`);
      setOrders((prev) => prev.filter((x) => x.id !== order.id));
      toast.success("Order deleted");
    } catch { toast.error("Delete failed"); }
  };

  const orderEmailLink = (order) => {
    const first = order.customer_name.split(" ")[0];
    const subject = `Star Jewels order ${order.id.slice(0, 8)} — update`;
    const body = `Hi ${first},\n\nWe wanted to update you on your Star Jewels order.\n\nOrder: ${order.id.slice(0, 8)}\nTotal: ₹${Number(order.total).toLocaleString("en-IN")}\n\nThank you for shopping with us!\n— Star Jewels`;
    return `mailto:${order.email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="admin-dashboard">
      {/* Sidebar + content shell */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] min-h-screen">
        <aside className="bg-sj-ink text-white p-6 lg:min-h-screen">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} className="h-10 w-10 object-contain" alt="" />
            <div>
              <div className="font-display font-medium tracking-wide">STAR JEWELS</div>
              <div className="overline text-[10px] text-sj-gold">Owner console</div>
            </div>
          </Link>

          <nav className="mt-10 flex lg:flex-col gap-1 overflow-x-auto">
            <button type="button" onClick={() => setTab("products")} data-testid="tab-products" className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm ${tab === "products" ? "bg-white/10 text-sj-gold" : "text-white/70 hover:text-white"}`}>
              <Package size={16} /> Products
            </button>
            <button type="button" onClick={() => setTab("orders")} data-testid="tab-orders" className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm ${tab === "orders" ? "bg-white/10 text-sj-gold" : "text-white/70 hover:text-white"}`}>
              <ShoppingCart size={16} /> Orders {orders.length > 0 && <span className="ml-auto text-[10px] bg-sj-gold text-white px-2 py-0.5 rounded-full">{orders.length}</span>}
            </button>
            <button type="button" onClick={() => setTab("collections")} data-testid="tab-collections" className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm ${tab === "collections" ? "bg-white/10 text-sj-gold" : "text-white/70 hover:text-white"}`}>
              <Layers size={16} /> Collections
            </button>
            <button type="button" onClick={() => setTab("banners")} data-testid="tab-banners" className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm ${tab === "banners" ? "bg-white/10 text-sj-gold" : "text-white/70 hover:text-white"}`}>
              <ImageIcon size={16} /> Home banner
            </button>
            <button type="button" onClick={() => setTab("site-images")} data-testid="tab-site-images" className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm ${tab === "site-images" ? "bg-white/10 text-sj-gold" : "text-white/70 hover:text-white"}`}>
              <Camera size={16} /> Site photos
            </button>
            <button type="button" onClick={() => setTab("occasions")} data-testid="tab-occasions" className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm ${tab === "occasions" ? "bg-white/10 text-sj-gold" : "text-white/70 hover:text-white"}`}>
              <Gift size={16} /> Shop by Occasion
            </button>
            <button type="button" onClick={() => setTab("style-looks")} data-testid="tab-style-looks" className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm ${tab === "style-looks" ? "bg-white/10 text-sj-gold" : "text-white/70 hover:text-white"}`}>
              <Palette size={16} /> Style Gallery
            </button>
            <button type="button" onClick={() => setTab("exchange-requests")} data-testid="tab-exchange-requests" className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm ${tab === "exchange-requests" ? "bg-white/10 text-sj-gold" : "text-white/70 hover:text-white"}`}>
              <MessageCircle size={16} /> Exchange Requests
            </button>
            <button type="button" onClick={() => setTab("reviews")} data-testid="tab-reviews" className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm ${tab === "reviews" ? "bg-white/10 text-sj-gold" : "text-white/70 hover:text-white"}`}>
              <Star size={16} /> Reviews
            </button>
            <button type="button" onClick={() => setTab("blog")} data-testid="tab-blog" className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm ${tab === "blog" ? "bg-white/10 text-sj-gold" : "text-white/70 hover:text-white"}`}>
              <Pencil size={16} /> Blog
            </button>
            <button type="button" onClick={() => setTab("settings")} data-testid="tab-settings" className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm ${tab === "settings" ? "bg-white/10 text-sj-gold" : "text-white/70 hover:text-white"}`}>
              <Sparkles size={16} /> Settings
            </button>
            <button type="button" onClick={() => setTab("color-swatches")} data-testid="tab-color-swatches" className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm ${tab === "color-swatches" ? "bg-white/10 text-sj-gold" : "text-white/70 hover:text-white"}`}>
              <Palette size={16} /> Color Swatches
            </button>
          </nav>

          <div className="mt-auto pt-10 lg:absolute lg:bottom-6 lg:left-6 lg:right-6 lg:w-[192px]">
            <div className="text-xs text-white/50 mb-3 truncate">Signed in as {user?.email}</div>
            <button type="button" onClick={doLogout} className="flex items-center gap-2 text-sm text-white/70 hover:text-sj-gold" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </aside>

        <main className="p-6 sm:p-10">
          {/* Products tab */}
          {tab === "products" && (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <div className="overline">Manage</div>
                  <h1 className="font-display text-3xl font-light">Products <span className="text-sj-muted text-lg">({products.length})</span></h1>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center bg-white rounded-full px-4 h-10 w-full sm:w-64 border border-sj-ink/10">
                    <Search size={14} className="text-sj-muted" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="bg-transparent flex-1 pl-2 text-sm focus:outline-none" data-testid="admin-search" />
                  </div>
                  <button type="button" onClick={openCreate} className="btn-gold text-sm" data-testid="admin-add-product">
                    <Plus size={16} /> Add product
                  </button>
                </div>
              </div>

              {loading ? (
                <StarLoader />
              ) : products.length === 0 ? (
                <div className="text-center bg-white rounded-md p-16 border border-sj-ink/5">
                  <div className="text-sj-muted">No products yet. Add your first piece.</div>
                  <button type="button" onClick={openCreate} className="btn-primary mt-6">
                    <Plus size={16} /> Add product
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="admin-products-grid">
                  {products.map((p) => (
                    <div key={p.id} className="bg-white rounded-md overflow-hidden border border-sj-ink/5 hover:shadow-lg transition-shadow" data-testid={`admin-product-${p.id}`}>
                      <div className="aspect-square bg-sj-cream overflow-hidden relative">
                        {p.images?.[0] ? <img src={resolveImage(p.images[0], { w: 400 })} alt="" className={`w-full h-full object-contain p-3 ${p.out_of_stock ? "opacity-60" : ""}`} loading="lazy" decoding="async" width="400" height="400" /> : <div className="w-full h-full flex items-center justify-center text-sj-muted text-xs">No image</div>}
                        {p.out_of_stock && (
                          <div className="absolute top-3 left-3 bg-sj-rose/95 text-white text-[10px] tracking-[0.2em] uppercase font-semibold px-2.5 py-1 rounded-full" data-testid={`admin-oos-badge-${p.id}`}>
                            Out of stock
                          </div>
                        )}
                        <button type="button" onClick={() => toggleFeatured(p)} className={`absolute top-3 right-3 p-2 rounded-full ${p.featured ? "bg-sj-gold text-white" : "bg-white/90 text-sj-inkSoft hover:text-sj-gold"} transition-colors`} data-testid={`admin-feature-${p.id}`}>
                          {p.featured ? <Star size={14} fill="currentColor" /> : <StarOff size={14} />}
                        </button>
                      </div>
                      <div className="p-4">
                        <div className="overline text-[9px]">{p.category}</div>
                        <div className="font-display text-base mt-1 line-clamp-1">{p.name}</div>
                        <div className="flex items-center justify-between mt-1">
                          <div className="text-sm">₹{Number(p.price).toLocaleString("en-IN")}</div>
                          <div className="text-xs text-sj-muted">Stock: {p.stock}</div>
                        </div>
                        <div className="mt-4 flex items-center gap-2">
                          <button type="button" onClick={() => openEdit(p)} className="flex-1 h-9 rounded-full border border-sj-ink/20 text-xs hover:border-sj-goldDark hover:text-sj-goldDark flex items-center justify-center gap-1" data-testid={`admin-edit-${p.id}`}>
                            <Pencil size={12} /> Edit
                          </button>
                          <button type="button" onClick={() => doDelete(p)} className="h-9 w-9 rounded-full border border-sj-ink/20 text-sj-inkSoft hover:border-sj-rose hover:text-sj-rose flex items-center justify-center" data-testid={`admin-delete-${p.id}`}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Orders tab */}
          {tab === "orders" && (
            <>
              <div className="mb-6">
                <div className="overline">All time</div>
                <h1 className="font-display text-3xl font-light">Orders <span className="text-sj-muted text-lg">({orders.length})</span></h1>
              </div>
              {orders.length === 0 ? (
                <div className="text-center bg-white rounded-md p-16 border border-sj-ink/5 text-sj-muted">
                  No orders yet.
                </div>
              ) : (
                <div className="space-y-4" data-testid="admin-orders-list">
                  {orders.map((o) => (
                    <details key={o.id} className="bg-white rounded-md border border-sj-ink/5 group" data-testid={`admin-order-${o.id}`}>
                      <summary className="p-5 flex items-center justify-between cursor-pointer list-none">
                        <div>
                          <div className="text-xs text-sj-muted">{new Date(o.created_at).toLocaleString()}</div>
                          <div className="font-display text-base mt-1">{o.customer_name} · {o.phone}</div>
                          <div className="text-xs text-sj-muted mt-0.5">{o.items.length} item{o.items.length > 1 ? "s" : ""} · Order {o.id.slice(0, 8)}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-display text-lg">₹{Number(o.total).toLocaleString("en-IN")}</div>
                          <div className={`text-[11px] mt-1 inline-block px-2 py-0.5 rounded-full uppercase tracking-wider ${
                            o.status === "delivered" || o.status === "completed" ? "bg-green-100 text-green-700"
                            : o.status === "shipped" ? "bg-blue-100 text-blue-700"
                            : "bg-amber-100 text-amber-700"
                          }`} data-testid={`admin-order-status-${o.id}`}>
                            {(o.status || "pending").replace(/_/g, " ")}
                          </div>
                        </div>
                      </summary>
                      <div className="border-t border-sj-ink/10 p-5 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                        <div>
                          <div className="overline mb-3">Ship to</div>
                          <div className="font-medium">{o.customer_name}</div>
                          <div className="text-sj-inkSoft">{o.address}</div>
                          <div className="text-sj-inkSoft">{o.city} — {o.pincode}</div>
                          <div className="mt-2 text-sj-inkSoft">📞 {o.phone}</div>
                          {o.email && <div className="text-sj-inkSoft">✉️ {o.email}</div>}
                          {o.notes && <div className="mt-3 p-3 bg-sj-cream rounded-md text-xs">Notes: {o.notes}</div>}
                        </div>
                        <div>
                          <div className="overline mb-3">Items</div>
                          <ul className="space-y-2">
                            {o.items.map((it, i) => (
                              <li key={i} className="flex justify-between">
                                <span className="text-sj-inkSoft">{it.name} × {it.quantity}</span>
                                <span>₹{(it.price * it.quantity).toLocaleString("en-IN")}</span>
                              </li>
                            ))}
                          </ul>
                          <div className="mt-3 pt-3 border-t border-sj-ink/10 flex justify-between font-medium">
                            <span>Total</span>
                            <span>₹{Number(o.total).toLocaleString("en-IN")}</span>
                          </div>
                          <div className="mt-5 flex items-center gap-2 flex-wrap">
                            <select
                              value={o.status || "pending"}
                              onChange={async (e) => {
                                try {
                                  await api.put(`/admin/orders/${o.id}/status`, { status: e.target.value });
                                  toast.success("Status updated");
                                  await load();
                                } catch { toast.error("Update failed"); }
                              }}
                              className="text-xs px-3 py-2 border border-sj-ink/15 rounded-md bg-white"
                              data-testid={`admin-order-status-select-${o.id}`}
                            >
                              <option value="pending">Pending</option>
                              <option value="shipped">Shipped</option>
                              <option value="delivered">Delivered</option>
                              <option value="completed">Completed</option>
                            </select>
                            <a
                              href={orderEmailLink(o)}
                              className="btn-gold text-xs"
                              data-testid={`admin-order-email-${o.id}`}
                            >
                              <Mail size={14} /> Send Email
                            </a>
                            <button type="button"
                              onClick={() => deleteOrder(o)}
                              className="ml-auto p-2 rounded-full border border-sj-ink/15 hover:border-sj-rose hover:text-sj-rose"
                              data-testid={`admin-delete-order-${o.id}`}
                              title="Delete order"
                              aria-label="Delete order"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </>
          )}
          {/* Collections tab */}
          {tab === "collections" && (
            <CollectionsPanel
              collections={collections}
              products={products}
              reload={load}
            />
          )}

          {/* Banners tab */}
          {tab === "banners" && (
            <BannersPanel
              banners={banners}
              bannerLinks={bannerLinks}
              mobileBanners={mobileBanners}
              collections={collections}
              products={products}
              reload={async () => {
                const { data } = await api.get("/banners");
                setBanners((data.slots || []).concat([null, null, null, null, null]).slice(0, 5));
                setBannerLinks((data.links || []).concat([null, null, null, null, null]).slice(0, 5));
                setMobileBanners((data.mobile_slots || []).concat([null, null, null, null, null]).slice(0, 5));
              }}
            />
          )}

          {/* Site photos tab */}
          {tab === "site-images" && (
            <SiteImagesPanel />
          )}

          {/* Shop by Occasion tab */}
          {tab === "occasions" && (
            <OccasionsPanel products={products} reload={load} />
          )}

          {/* Style Gallery tab */}
          {tab === "style-looks" && (
            <StyleLooksPanel products={products} />
          )}

          {/* Exchange Requests tab */}
          {tab === "exchange-requests" && (
            <ExchangeRequestsPanel />
          )}

          {/* Reviews tab */}
          {tab === "reviews" && (
            <ReviewsPanel />
          )}

          {/* Blog tab */}
          {tab === "blog" && (
            <BlogPanel />
          )}

          {/* Settings tab */}
          {tab === "settings" && (
            <SettingsPanel />
          )}

          {/* Color Swatches tab */}
          {tab === "color-swatches" && (
            <ColorSwatchesPanel />
          )}
        </main>
      </div>

      {/* Product Editor Modal */}
      <AnimatePresence>
        {editorOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-sj-ink/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={closeEditor}
            data-testid="admin-product-editor"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-md max-h-[92vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <form onSubmit={save}>
                <div className="p-6 sm:p-8 border-b border-sj-ink/10 flex items-center justify-between sticky top-0 bg-white z-10">
                  <div>
                    <div className="overline">{editing ? "Edit" : "Add"}</div>
                    <h2 className="font-display text-xl mt-1">{editing ? "Edit product" : "New product"}</h2>
                  </div>
                  <button type="button" onClick={closeEditor} className="p-2 text-sj-muted hover:text-sj-ink" data-testid="editor-close">
                    <X size={20} />
                  </button>
                </div>

                <div className="p-6 sm:p-8 space-y-5">
                  <div>
                    <label className="text-xs text-sj-inkSoft">Product name</label>
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm" data-testid="editor-name" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-sj-inkSoft">Selling Price (₹)</label>
                      <input required type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                        className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm" data-testid="editor-price" />
                    </div>
                    <div>
                      <label className="text-xs text-sj-inkSoft">Stock</label>
                      <input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })}
                        className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm" data-testid="editor-stock" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-sj-inkSoft">
                      MRP (₹) <span className="text-sj-muted">— optional. When set higher than selling price, a strikethrough discount is shown.</span>
                    </label>
                    <input type="number" min="0" step="0.01" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })}
                      placeholder="e.g. 1999"
                      className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm" data-testid="editor-original-price" />
                  </div>

                  <div>
                    <label className="text-xs text-sj-inkSoft">Category</label>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm" data-testid="editor-category">
                      {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-sj-inkSoft">Description</label>
                      <button type="button" onClick={generateAI} disabled={aiBusy} className="text-xs text-sj-goldDark hover:text-sj-gold font-medium inline-flex items-center gap-1 disabled:opacity-50" data-testid="editor-ai-generate">
                        <Sparkles size={12} /> {aiBusy ? "Drafting…" : "AI draft"}
                      </button>
                    </div>
                    <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Write a short, warm description — or tap AI draft above."
                      className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 py-3 text-sm" data-testid="editor-description" />
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-sj-inkSoft">Moon's styling tip <span className="text-sj-muted">(optional — leave blank to use a category-based default)</span></label>
                      <button
                        type="button"
                        onClick={generateStylingTip}
                        disabled={tipBusy}
                        className="text-xs text-sj-goldDark hover:text-sj-gold font-medium inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid="editor-ai-styling-tip"
                        title="Moon analyses the product photo(s) and drafts a tip specific to what she sees."
                      >
                        <Sparkles size={12} /> {tipBusy ? "Analysing photo…" : "Ask Moon (uses photo)"}
                      </button>
                    </div>
                    <input
                      type="text"
                      value={form.styling_tip}
                      onChange={(e) => setForm({ ...form, styling_tip: e.target.value })}
                      placeholder="e.g. Pair these earrings with pastel outfits for a soft, elegant look."
                      className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm"
                      data-testid="editor-styling-tip"
                    />
                  </div>

                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="editor-featured-label">
                    <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-sj-gold" data-testid="editor-featured" />
                    <span>Feature on the home page</span>
                  </label>

                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="editor-oos-label">
                    <input type="checkbox" checked={form.out_of_stock} onChange={(e) => setForm({ ...form, out_of_stock: e.target.checked })} className="accent-sj-rose" data-testid="editor-out-of-stock" />
                    <span>Out of stock <span className="text-xs text-sj-muted">(hidden from shop; direct link shows blurred image with Notify Me)</span></span>
                  </label>

                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="editor-lowstock-label">
                    <input type="checkbox" checked={form.low_stock} onChange={(e) => setForm({ ...form, low_stock: e.target.checked })} className="accent-sj-goldDark" data-testid="editor-low-stock" />
                    <span>Few pieces left <span className="text-xs text-sj-muted">(shows a scarcity badge on product cards & PDP)</span></span>
                  </label>

                  {/* Collections multiselect */}
                  <div>
                    <label className="text-xs text-sj-inkSoft">Collections</label>
                    <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="editor-collections">
                      {collections.length === 0 && <span className="text-xs text-sj-muted">No collections yet. Create some in the Collections tab.</span>}
                      {collections.map((c) => {
                        const selected = (form.collection_ids || []).includes(c.id);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => setForm((f) => ({
                              ...f,
                              collection_ids: selected
                                ? f.collection_ids.filter((id) => id !== c.id)
                                : [...(f.collection_ids || []), c.id],
                            }))}
                            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${selected ? "bg-sj-gold text-white border-sj-gold" : "bg-white text-sj-ink border-sj-ink/15 hover:border-sj-goldDark"}`}
                            data-testid={`editor-collection-${c.id}`}
                          >
                            {c.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Occasions multiselect — powers the Occasion filter on the Shop page */}
                  <div>
                    <label className="text-xs text-sj-inkSoft">
                      Occasions <span className="text-sj-muted">(Diwali, Wedding, Casual, etc. — powers the Occasion filter on Shop)</span>
                    </label>
                    <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="editor-occasions">
                      {occasions.length === 0 && <span className="text-xs text-sj-muted">No occasions yet. Create some in the Occasions tab.</span>}
                      {occasions.map((o) => {
                        const selected = (form.occasions || []).includes(o.id);
                        return (
                          <button
                            key={o.id}
                            type="button"
                            onClick={() => setForm((f) => ({
                              ...f,
                              occasions: selected
                                ? (f.occasions || []).filter((id) => id !== o.id)
                                : [...(f.occasions || []), o.id],
                            }))}
                            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${selected ? "bg-sj-goldDark text-white border-sj-goldDark" : "bg-white text-sj-ink border-sj-ink/15 hover:border-sj-goldDark"}`}
                            data-testid={`editor-occasion-${o.id}`}
                          >
                            {o.title}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Bundle With — Moon's Pairings admin override (max 2) */}
                  <div>
                    <label className="text-xs text-sj-inkSoft">
                      Bundle With — Moon's Pairings <span className="text-sj-muted">(pick up to 2 companion products; leave empty for Moon AI auto-pick)</span>
                    </label>
                    <BundleWithPicker
                      allProducts={products}
                      currentProductId={editing?.id}
                      value={form.bundle_with || []}
                      onChange={(next) => setForm((f) => ({ ...f, bundle_with: next }))}
                    />
                  </div>

                  {/* Images */}
                  <div>
                    <label className="text-xs text-sj-inkSoft">Photos</label>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {existingImgs.map((path, idx) => (
                        <div key={path} className="relative bg-sj-cream group rounded-md overflow-hidden border border-sj-ink/10">
                          <div className="aspect-square overflow-hidden">
                            <img src={resolveImage(path, { w: 400 })} alt="" className="w-full h-full object-contain p-2" loading="lazy" decoding="async" />
                          </div>
                          <button type="button" onClick={() => removeExistingImg(path)} className="absolute top-1 right-1 p-1 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-testid="editor-remove-existing-img">
                            <X size={12} className="text-sj-rose" />
                          </button>
                          <div className="p-2 border-t border-sj-ink/5">
                            <input
                              type="text"
                              value={existingColors[idx] || ""}
                              onChange={(e) => setExistingColors((prev) => prev.map((c, i) => i === idx ? e.target.value : c))}
                              placeholder="Color tag (e.g. Gold)"
                              className="w-full bg-white border border-sj-ink/10 rounded px-2 h-8 text-xs outline-none focus:border-sj-goldDark"
                              data-testid={`editor-existing-color-${idx}`}
                            />
                          </div>
                        </div>
                      ))}
                      {files.map((f, i) => (
                        <div key={i} className="relative bg-sj-cream group rounded-md overflow-hidden border border-sj-ink/10">
                          <div className="aspect-square overflow-hidden">
                            <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-contain p-2" />
                          </div>
                          <div className="absolute top-1 right-1 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() => setRecropIdx(i)}
                              className="p-1 bg-white/90 rounded-full text-sj-goldDark hover:text-sj-gold"
                              title="Re-crop"
                              data-testid={`editor-recrop-${i}`}
                            >
                              <CropIcon size={12} />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeNewFile(i)}
                              className="p-1 bg-white/90 rounded-full text-sj-rose"
                              title="Remove"
                            >
                              <X size={12} />
                            </button>
                          </div>
                          <div className="absolute bottom-8 inset-x-0 bg-sj-gold text-white text-[10px] text-center py-0.5">NEW</div>
                          <div className="p-2 border-t border-sj-ink/5">
                            <input
                              type="text"
                              value={fileColors[i] || ""}
                              onChange={(e) => setFileColors((prev) => prev.map((c, idx) => idx === i ? e.target.value : c))}
                              placeholder="Color tag (e.g. Silver)"
                              className="w-full bg-white border border-sj-ink/10 rounded px-2 h-8 text-xs outline-none focus:border-sj-goldDark"
                              data-testid={`editor-file-color-${i}`}
                            />
                          </div>
                        </div>
                      ))}
                      <label className="aspect-square border-2 border-dashed border-sj-ink/20 rounded-md flex flex-col items-center justify-center text-sj-muted hover:border-sj-goldDark hover:text-sj-goldDark cursor-pointer transition-colors" data-testid="editor-upload-label">
                        <Upload size={18} />
                        <span className="text-xs mt-2">Add photo</span>
                        <input type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="editor-file-input" />
                      </label>
                    </div>
                    <div className="text-xs text-sj-muted mt-2">Tag a color on each photo (Gold / Silver / any custom name) — customers see colored swatches on the product page and can filter photos by color.</div>
                  </div>
                </div>

                <div className="p-6 sm:p-8 border-t border-sj-ink/10 flex items-center justify-end gap-3 sticky bottom-0 bg-white">
                  <button type="button" onClick={closeEditor} className="btn-outline text-sm" data-testid="editor-cancel">Cancel</button>
                  <button type="submit" disabled={busy} className="btn-primary text-sm disabled:opacity-60" data-testid="editor-save">
                    {busy ? "Saving…" : editing ? "Save changes" : "Add product"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Crop & resize modal — appears above the product editor */}
      <CropModal
        file={currentCropFile}
        onCancel={onCropCancelled}
        onSave={onCropSaved}
      />
    </div>
  );
}


/* -------------------- Collections Panel -------------------- */
function CollectionsPanel({ collections, products, reload }) {
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [rename, setRename] = useState("");
  const [busy, setBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [coverTarget, setCoverTarget] = useState(null); // { file, collectionId }
  const [productSearch, setProductSearch] = useState("");

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/collections", { name: newName.trim(), product_ids: [] });
      setNewName("");
      toast.success("Collection created");
      await reload();
    } catch { toast.error("Create failed"); } finally { setBusy(false); }
  };

  const saveRename = async (id) => {
    if (!rename.trim()) return;
    try {
      await api.put(`/admin/collections/${id}`, { name: rename.trim() });
      setEditingId(null); setRename("");
      toast.success("Renamed");
      await reload();
    } catch { toast.error("Rename failed"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Products will remain, just unlinked.`)) return;
    try {
      await api.delete(`/admin/collections/${c.id}`);
      toast.success("Deleted");
      await reload();
    } catch { toast.error("Delete failed"); }
  };

  const toggleProduct = async (c, productId) => {
    const has = (c.product_ids || []).includes(productId);
    const next = has
      ? (c.product_ids || []).filter((id) => id !== productId)
      : [...(c.product_ids || []), productId];
    try {
      await api.put(`/admin/collections/${c.id}`, { product_ids: next });
      await reload();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="admin-collections-panel">
      <div className="mb-6">
        <div className="overline">Manage</div>
        <h1 className="font-display text-3xl font-light">Collections <span className="text-sj-muted text-lg">({collections.length})</span></h1>
        <p className="text-sm text-sj-muted mt-2">Group products under named collections (Pashmina, Madhubani, Warli, custom names). These show on the home page.</p>
      </div>

      <form onSubmit={create} className="mb-8 flex items-center gap-3 bg-white p-4 rounded-md border border-sj-ink/5">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New collection name (e.g. Signature)"
          className="flex-1 bg-transparent outline-none px-2 text-sm"
          data-testid="admin-new-collection-input"
        />
        <button type="submit" disabled={busy} className="btn-primary text-sm disabled:opacity-60" data-testid="admin-new-collection-btn">
          <Plus size={14} /> Add collection
        </button>
      </form>

      <div className="space-y-3">
        {collections.map((c) => {
          const assigned = products.filter((p) => (c.product_ids || []).includes(p.id));
          return (
            <div key={c.id} className="bg-white rounded-md border border-sj-ink/5" data-testid={`admin-collection-${c.id}`}>
              <div className="p-5 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                  {c.cover_image ? (
                    <img src={resolveImage(c.cover_image, { w: 150 })} alt="" className="h-14 w-14 rounded-md object-cover" data-testid={`admin-collection-cover-${c.id}`} loading="lazy" decoding="async" />
                  ) : (
                    <div className="h-14 w-14 rounded-md bg-sj-cream flex items-center justify-center text-sj-muted">
                      <ImageIcon size={16} />
                    </div>
                  )}
                  {editingId === c.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input value={rename} onChange={(e) => setRename(e.target.value)} className="bg-sj-cream rounded-md px-3 h-9 text-sm outline-none flex-1 max-w-md" data-testid={`admin-collection-rename-input-${c.id}`} />
                      <button type="button" onClick={() => saveRename(c.id)} className="btn-gold text-xs" data-testid={`admin-collection-save-${c.id}`}>Save</button>
                      <button type="button" onClick={() => { setEditingId(null); setRename(""); }} className="text-xs text-sj-muted">Cancel</button>
                    </div>
                  ) : (
                    <div>
                      <div className="font-display text-lg">{c.name}</div>
                      <div className="text-xs text-sj-muted mt-1">{assigned.length} product{assigned.length === 1 ? "" : "s"}</div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <label className="btn-outline text-xs cursor-pointer" data-testid={`admin-collection-cover-upload-${c.id}`}>
                    <Upload size={12} /> {c.cover_image ? "Replace cover" : "Set cover"}
                    <input
                      type="file" accept="image/*" className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (!file) return;
                        setCoverTarget({ file, collectionId: c.id });
                      }}
                    />
                  </label>
                  <button type="button" onClick={() => { setExpandedId(expandedId === c.id ? null : c.id); setProductSearch(""); }} className="btn-outline text-xs" data-testid={`admin-collection-assign-${c.id}`}>
                    <ChevronDown size={14} className={expandedId === c.id ? "rotate-180 transition-transform" : "transition-transform"} /> Assign products
                  </button>
                  <button type="button" onClick={() => { setEditingId(c.id); setRename(c.name); }} className="p-2 rounded-full border border-sj-ink/15 hover:border-sj-goldDark hover:text-sj-goldDark" data-testid={`admin-collection-edit-${c.id}`}>
                    <Pencil size={13} />
                  </button>
                  <button type="button" onClick={() => del(c)} className="p-2 rounded-full border border-sj-ink/15 hover:border-sj-rose hover:text-sj-rose" data-testid={`admin-collection-delete-${c.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              {expandedId === c.id && (
                <div className="border-t border-sj-ink/10 p-5">
                  {products.length === 0 ? (
                    <div className="text-sm text-sj-muted">Create some products first.</div>
                  ) : (() => {
                    const q = productSearch.trim().toLowerCase();
                    const filtered = q ? products.filter((p) => (p.name || "").toLowerCase().includes(q)) : products;
                    return (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="relative flex-1">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-sj-muted" />
                            <input
                              type="text"
                              value={productSearch}
                              onChange={(e) => setProductSearch(e.target.value)}
                              placeholder="Search products by name…"
                              className="w-full pl-9 pr-3 py-2 border border-sj-ink/15 rounded text-sm outline-none focus:border-sj-goldDark"
                              data-testid={`admin-collection-product-search-${c.id}`}
                            />
                          </div>
                          <span className="text-xs text-sj-muted whitespace-nowrap">{filtered.length} / {products.length}</span>
                        </div>
                        {filtered.length === 0 ? (
                          <div className="text-xs text-sj-muted py-6 text-center">No products match "{productSearch}".</div>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[420px] overflow-y-auto pr-1">
                            {filtered.map((p) => {
                              const has = (c.product_ids || []).includes(p.id);
                              return (
                                <button type="button"
                                  key={p.id}
                                  onClick={() => toggleProduct(c, p.id)}
                                  className={`text-left rounded-md border overflow-hidden transition-all ${has ? "border-sj-gold ring-2 ring-sj-gold/40" : "border-sj-ink/10 hover:border-sj-goldDark"}`}
                                  data-testid={`admin-col-${c.id}-toggle-${p.id}`}
                                >
                                  <div className="aspect-square bg-sj-cream overflow-hidden">
                                    {p.images?.[0] && <img src={resolveImage(p.images[0], { w: 300 })} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />}
                                  </div>
                                  <div className="p-2">
                                    <div className="text-xs font-medium line-clamp-1">{p.name}</div>
                                    <div className="text-[11px] text-sj-muted">₹{Number(p.price).toLocaleString("en-IN")}</div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <CropModal
        file={coverTarget?.file || null}
        title="Crop collection cover"
        aspects={[
          { label: "3:4", value: 3 / 4 },
          { label: "1:1", value: 1 },
          { label: "4:5", value: 4 / 5 },
          { label: "Free", value: null },
        ]}
        onCancel={() => setCoverTarget(null)}
        onSave={async (cropped) => {
          const { collectionId } = coverTarget;
          setCoverTarget(null);
          try {
            const fd = new FormData();
            fd.append("file", cropped);
            await api.post(`/admin/collections/${collectionId}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Cover updated");
            await reload();
          } catch { toast.error("Upload failed"); }
        }}
      />
    </div>
  );
}

/* -------------------- Banners Panel -------------------- */
function BannersPanel({ banners, bannerLinks, mobileBanners, collections, products, reload }) {
  const [busyIdx, setBusyIdx] = useState(null); // {idx, variant}
  const [cropTarget, setCropTarget] = useState(null); // { file, idx, variant }

  const doUpload = async (idx, variant, file) => {
    setBusyIdx({ idx, variant });
    try {
      const fd = new FormData();
      fd.append("index", String(idx));
      fd.append("variant", variant);
      fd.append("file", file);
      await api.post("/admin/banners/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Slot ${idx + 1} ${variant} updated`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusyIdx(null); }
  };

  const onPick = (idx, variant, file) => {
    if (!file) return;
    setCropTarget({ file, idx, variant });
  };

  const clearSlot = async (idx) => {
    if (!window.confirm(`Clear banner slot ${idx + 1}?`)) return;
    try {
      const slots = [...banners];
      slots[idx] = null;
      await api.put("/admin/banners", { slots });
      toast.success("Slot cleared");
      await reload();
    } catch { toast.error("Update failed"); }
  };

  const updateLink = async (idx, value) => {
    const links = [...bannerLinks];
    links[idx] = value || null;
    try {
      await api.put("/admin/banners", { links });
      await reload();
    } catch { toast.error("Failed to save link"); }
  };

  return (
    <div data-testid="admin-banners-panel">
      <div className="mb-6">
        <div className="overline">Manage</div>
        <h1 className="font-display text-3xl font-light">Home banner</h1>
        <p className="text-sm text-sj-muted mt-2">
          Upload up to 5 images at <span className="font-semibold text-sj-ink">3:1 ratio</span> (e.g. 3000×1000 px).
          This single ratio is used on both desktop and mobile. Slides rotate automatically every 7 seconds.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {banners.map((path, idx) => (
          <div key={idx} className="bg-white rounded-md border border-sj-ink/5 overflow-hidden" data-testid={`admin-banner-slot-${idx}`}>
            <div className="relative aspect-[3/1] bg-sj-cream">
              {path ? (
                <img src={resolveImage(path)} alt="" className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-sj-muted">
                  <ImageIcon size={22} />
                  <div className="text-xs mt-2">Empty slot</div>
                </div>
              )}
              <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold text-sj-goldDark">
                Slot {idx + 1}
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-sj-muted mb-1.5">Desktop image (3:1 wide)</div>
                <div className="flex items-center gap-2">
                  <label className="btn-outline text-xs cursor-pointer flex-1 justify-center" data-testid={`admin-banner-upload-label-${idx}`}>
                    <Upload size={12} /> {busyIdx?.idx === idx && busyIdx?.variant === "desktop" ? "Uploading…" : (path ? "Replace" : "Upload")}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={busyIdx?.idx === idx && busyIdx?.variant === "desktop"}
                      onChange={(e) => { onPick(idx, "desktop", e.target.files?.[0]); e.target.value = ""; }}
                      data-testid={`admin-banner-upload-input-${idx}`}
                    />
                  </label>
                  {path && (
                    <button type="button" onClick={() => clearSlot(idx)} className="p-2 rounded-full border border-sj-ink/15 hover:border-sj-rose hover:text-sj-rose" data-testid={`admin-banner-clear-${idx}`}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-sj-muted mb-1.5">Mobile image (4:5 or 1:1 optional)</div>
                <div className="flex items-center gap-2">
                  <div className="w-14 h-16 rounded bg-sj-cream overflow-hidden shrink-0">
                    {mobileBanners?.[idx] ? (
                      <img src={resolveImage(mobileBanners[idx], { w: 200 })} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><ImageIcon size={14} className="text-sj-muted" /></div>
                    )}
                  </div>
                  <label className="btn-outline text-xs cursor-pointer flex-1 justify-center" data-testid={`admin-banner-mobile-upload-label-${idx}`}>
                    <Upload size={12} /> {busyIdx?.idx === idx && busyIdx?.variant === "mobile" ? "Uploading…" : (mobileBanners?.[idx] ? "Replace" : "Upload")}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={busyIdx?.idx === idx && busyIdx?.variant === "mobile"}
                      onChange={(e) => { onPick(idx, "mobile", e.target.files?.[0]); e.target.value = ""; }}
                      data-testid={`admin-banner-mobile-upload-input-${idx}`}
                    />
                  </label>
                </div>
              </div>
            </div>
            <div className="px-4 pb-4">
              <label className="text-[10px] uppercase tracking-wider text-sj-muted">Link to page (click destination)</label>
              <select
                value={bannerLinks[idx] || ""}
                onChange={(e) => updateLink(idx, e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-sj-ink/15 rounded text-sm bg-white"
                data-testid={`admin-banner-link-${idx}`}
              >
                <option value="">— No link (not clickable) —</option>
                <option value="/shop">Shop (all products)</option>
                {(collections || []).map((c) => (
                  <option key={`c-${c.id}`} value={`/shop?collection=${c.id}`}>Collection: {c.name}</option>
                ))}
                {CATEGORIES.map((cat) => (
                  <option key={`cat-${cat.key}`} value={`/shop?category=${encodeURIComponent(cat.label)}`}>Category: {cat.label}</option>
                ))}
                {(products || []).slice(0, 200).map((p) => (
                  <option key={`p-${p.id}`} value={`/product/${p.id}`}>Product: {p.name}</option>
                ))}
                <option value="/style">Page: Style Inspiration</option>
                <option value="/about">Page: About</option>
              </select>
            </div>
          </div>
        ))}
      </div>

      <CropModal
        file={cropTarget?.file || null}
        title={cropTarget?.variant === "mobile" ? "Crop mobile banner" : "Crop banner (3:1)"}
        aspects={cropTarget?.variant === "mobile" ? [
          { label: "4:5", value: 4 / 5 },
          { label: "1:1", value: 1 },
          { label: "3:4", value: 3 / 4 },
          { label: "Free", value: null },
        ] : [
          { label: "3:1", value: 3 },
          { label: "2.6:1", value: 13 / 5 },
          { label: "16:9", value: 16 / 9 },
          { label: "Free", value: null },
        ]}
        onCancel={() => setCropTarget(null)}
        onSave={async (cropped) => {
          const { idx, variant } = cropTarget;
          setCropTarget(null);
          await doUpload(idx, variant, cropped);
        }}
      />
    </div>
  );
}


/* -------------------- Site Images Panel (About/Story) -------------------- */
function SiteImagesPanel() {
  const [images, setImages] = useState({});
  const [busy, setBusy] = useState(null); // key currently uploading
  const [cropTarget, setCropTarget] = useState(null); // { file, key }

  const load = async () => {
    try {
      const { data } = await api.get("/site-images");
      setImages(data || {});
    } catch { setImages({}); }
  };
  useEffect(() => { load(); }, []);

  const doUpload = async (key, file) => {
    setBusy(key);
    try {
      const fd = new FormData();
      fd.append("key", key);
      fd.append("file", file);
      await api.post("/admin/site-images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${key} image updated`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(null); }
  };

  const onPick = (key, file) => {
    if (!file) return;
    setCropTarget({ file, key });
  };

  const clear = async (key) => {
    if (!window.confirm(`Reset the ${key} photo to the default?`)) return;
    try {
      await api.delete(`/admin/site-images/${key}`);
      toast.success("Reset");
      await load();
    } catch { toast.error("Reset failed"); }
  };

  const slots = [
    { key: "about", label: "About / Story photo", hint: "Shown on both the home 'Our story' section and the About page." },
    { key: "moon_avatar", label: "Moon's avatar", hint: "Small circular photo used beside 'Styled by Moon' and Moon's styling tips. Square/round crop works best." },
  ];

  return (
    <div data-testid="admin-site-images-panel">
      <div className="mb-6">
        <div className="overline">Manage</div>
        <h1 className="font-display text-3xl font-light">Site photos</h1>
        <p className="text-sm text-sj-muted mt-2">Replace any hero or story photo across the site. Product & collection covers have their own upload buttons in their tabs.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {slots.map(({ key, label, hint }) => (
          <div key={key} className="bg-white rounded-md border border-sj-ink/5 overflow-hidden" data-testid={`admin-site-image-${key}`}>
            <div className="relative aspect-[4/3] bg-sj-cream">
              {images[key] ? (
                <img src={resolveImage(images[key], { w: 800 })} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-sj-muted">
                  <ImageIcon size={22} />
                  <div className="text-xs mt-2">Using default</div>
                </div>
              )}
              <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold text-sj-goldDark">
                {label}
              </div>
            </div>
            <div className="p-4">
              <div className="text-xs text-sj-muted mb-3">{hint}</div>
              <div className="flex items-center gap-2">
                <label className="btn-outline text-xs cursor-pointer flex-1 justify-center" data-testid={`admin-site-image-upload-${key}`}>
                  <Upload size={12} /> {busy === key ? "Uploading…" : (images[key] ? "Replace" : "Upload")}
                  <input type="file" accept="image/*" className="hidden" disabled={busy === key}
                    onChange={(e) => { onPick(key, e.target.files?.[0]); e.target.value = ""; }}
                    data-testid={`admin-site-image-input-${key}`} />
                </label>
                {images[key] && (
                  <button type="button" onClick={() => clear(key)} className="p-2 rounded-full border border-sj-ink/15 hover:border-sj-rose hover:text-sj-rose" data-testid={`admin-site-image-clear-${key}`}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <CropModal
        file={cropTarget?.file || null}
        title="Crop story photo"
        aspects={[
          { label: "1:1", value: 1 },
          { label: "4:5", value: 4 / 5 },
          { label: "Free", value: null },
        ]}
        onCancel={() => setCropTarget(null)}
        onSave={async (cropped) => {
          const { key } = cropTarget;
          setCropTarget(null);
          await doUpload(key, cropped);
        }}
      />
    </div>
  );
}



/* -------------------- Occasions Panel (Shop by Occasion) -------------------- */
function OccasionsPanel({ products = [], reload }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [collections, setCollections] = useState([]);
  const [cropTarget, setCropTarget] = useState(null); // { file, id }
  const [expandedId, setExpandedId] = useState(null);
  const [productSearch, setProductSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [o, c] = await Promise.all([api.get("/occasions"), api.get("/collections")]);
      setItems(o.data || []);
      setCollections(c.data || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addOccasion = async () => {
    try {
      await api.post("/admin/occasions", { title: "New Occasion", subtitle: "", link: "" });
      toast.success("Added");
      await load();
    } catch { toast.error("Failed to add"); }
  };

  const patch = async (id, patchBody) => {
    try {
      await api.put(`/admin/occasions/${id}`, patchBody);
      await load();
    } catch { toast.error("Update failed"); }
  };

  const removeOccasion = async (id) => {
    if (!window.confirm("Delete this occasion card?")) return;
    try {
      await api.delete(`/admin/occasions/${id}`);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  const uploadImage = async (id, file) => {
    setBusyId(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/occasions/${id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image updated");
      await load();
    } catch { toast.error("Upload failed"); }
    finally { setBusyId(null); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const order = [...items];
    [order[idx], order[j]] = [order[j], order[idx]];
    try {
      await api.put("/admin/occasions/reorder", { order: order.map((o) => o.id) });
      await load();
    } catch { toast.error("Reorder failed"); }
  };

  // Toggle whether a product carries this occasion id in its `occasions[]` field.
  const toggleProductOccasion = async (occasionId, product) => {
    const cur = new Set(product.occasions || []);
    if (cur.has(occasionId)) cur.delete(occasionId); else cur.add(occasionId);
    try {
      await api.put(`/admin/products/${product.id}`, { occasions: Array.from(cur) });
      if (reload) await reload();
    } catch { toast.error("Update failed"); }
  };

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [products, productSearch]);

  return (
    <div data-testid="admin-occasions-panel">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Manage</div>
          <h1 className="font-display text-3xl font-light">Shop by Occasion</h1>
          <p className="text-sm text-sj-muted mt-2">Featured on the homepage below the hero banner. Add, edit, reorder, or delete occasion cards.</p>
        </div>
        <button type="button" onClick={addOccasion} className="btn-primary text-sm" data-testid="admin-occasion-add"><Plus size={14} /> Add occasion</button>
      </div>

      {loading ? (
        <StarLoader />
      ) : items.length === 0 ? (
        <div className="text-sj-muted text-sm py-10 text-center border border-dashed border-sj-ink/10 rounded-md">No occasions yet. Click "Add occasion" to get started.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {items.map((o, idx) => (
            <div key={o.id} className="bg-white rounded-md border border-sj-ink/5 overflow-hidden p-4" data-testid={`admin-occasion-${o.id}`}>
              <div className="grid grid-cols-1 sm:grid-cols-[160px,1fr] gap-4">
                <div>
                  <div className="relative aspect-[3/4] bg-sj-cream rounded overflow-hidden">
                    {o.image ? (
                      <img src={resolveImage(o.image, { w: 400 })} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-sj-muted">
                        <ImageIcon size={20} />
                        <div className="text-[10px] mt-1">No image</div>
                      </div>
                    )}
                  </div>
                  <label className="btn-outline text-xs cursor-pointer w-full justify-center mt-2" data-testid={`admin-occasion-image-${o.id}`}>
                    <Upload size={12} /> {busyId === o.id ? "Uploading…" : (o.image ? "Replace image" : "Upload image")}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={busyId === o.id}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) setCropTarget({ file: f, id: o.id }); e.target.value = ""; }}
                    />
                  </label>
                </div>

                <div className="space-y-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-sj-muted">Title</label>
                    <input
                      type="text"
                      defaultValue={o.title}
                      onBlur={(e) => e.target.value !== o.title && patch(o.id, { title: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-sj-ink/15 rounded text-sm"
                      data-testid={`admin-occasion-title-${o.id}`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-sj-muted">Subtitle</label>
                    <input
                      type="text"
                      defaultValue={o.subtitle || ""}
                      onBlur={(e) => e.target.value !== (o.subtitle || "") && patch(o.id, { subtitle: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-sj-ink/15 rounded text-sm"
                      data-testid={`admin-occasion-subtitle-${o.id}`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-sj-muted">Link (Shop Now goes to)</label>
                    <select
                      defaultValue={o.link || ""}
                      onChange={(e) => patch(o.id, { link: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-sj-ink/15 rounded text-sm bg-white"
                      data-testid={`admin-occasion-link-${o.id}`}
                    >
                      <option value="">— Shop (all products) —</option>
                      {collections.map((c) => (
                        <option key={c.id} value={`/shop?collection=${c.id}`}>Collection: {c.name}</option>
                      ))}
                      {CATEGORIES.map((cat) => (
                        <option key={cat.key} value={`/shop?category=${encodeURIComponent(cat.label)}`}>Category: {cat.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="p-2 rounded border border-sj-ink/15 disabled:opacity-40" data-testid={`admin-occasion-up-${o.id}`}><ArrowUp size={13} /></button>
                    <button type="button" onClick={() => move(idx, +1)} disabled={idx === items.length - 1} className="p-2 rounded border border-sj-ink/15 disabled:opacity-40" data-testid={`admin-occasion-down-${o.id}`}><ArrowDown size={13} /></button>
                    <button
                      type="button"
                      onClick={() => { setExpandedId(expandedId === o.id ? null : o.id); setProductSearch(""); }}
                      className="btn-outline text-xs"
                      data-testid={`admin-occasion-assign-${o.id}`}
                    >
                      <ChevronDown size={13} className={expandedId === o.id ? "rotate-180 transition-transform" : "transition-transform"} /> Assign products ({(products || []).filter(p => (p.occasions || []).includes(o.id)).length})
                    </button>
                    <div className="flex-1" />
                    <button type="button" onClick={() => removeOccasion(o.id)} className="p-2 rounded border border-sj-ink/15 hover:border-sj-rose hover:text-sj-rose" data-testid={`admin-occasion-delete-${o.id}`}><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>

              {expandedId === o.id && (
                <div className="mt-4 border-t border-sj-ink/10 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="relative flex-1">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-sj-muted" />
                      <input
                        type="text"
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        placeholder="Search products by name…"
                        className="w-full pl-9 pr-3 py-2 border border-sj-ink/15 rounded text-sm outline-none focus:border-sj-goldDark"
                        data-testid={`admin-occasion-product-search-${o.id}`}
                      />
                    </div>
                    <span className="text-xs text-sj-muted whitespace-nowrap">{filteredProducts.length} / {products.length}</span>
                  </div>
                  {filteredProducts.length === 0 ? (
                    <div className="text-xs text-sj-muted py-6 text-center">No products match "{productSearch}".</div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[420px] overflow-y-auto pr-1">
                      {filteredProducts.map((p) => {
                        const has = (p.occasions || []).includes(o.id);
                        return (
                          <button
                            type="button"
                            key={p.id}
                            onClick={() => toggleProductOccasion(o.id, p)}
                            className={`text-left rounded-md border overflow-hidden transition-all ${has ? "border-sj-gold ring-2 ring-sj-gold/40" : "border-sj-ink/10 hover:border-sj-goldDark"}`}
                            data-testid={`admin-occ-${o.id}-toggle-${p.id}`}
                          >
                            <div className="aspect-square bg-sj-cream overflow-hidden">
                              {p.images?.[0] && <img src={resolveImage(p.images[0], { w: 300 })} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />}
                            </div>
                            <div className="p-2">
                              <div className="text-xs font-medium line-clamp-1">{p.name}</div>
                              <div className="text-[11px] text-sj-muted">₹{Number(p.price).toLocaleString("en-IN")}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <CropModal
        file={cropTarget?.file || null}
        title="Crop occasion image"
        aspects={[
          { label: "3:4", value: 3 / 4 },
          { label: "1:1", value: 1 },
          { label: "4:5", value: 4 / 5 },
          { label: "Free", value: null },
        ]}
        onCancel={() => setCropTarget(null)}
        onSave={async (cropped) => {
          const { id } = cropTarget;
          setCropTarget(null);
          await uploadImage(id, cropped);
        }}
      />
    </div>
  );
}


/* -------------------- Style Looks Panel (Lifestyle Gallery) -------------------- */
function StyleLooksPanel({ products }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [cropTarget, setCropTarget] = useState(null);
  const [productSearch, setProductSearch] = useState({}); // { [lookId]: query }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/style-looks");
      setItems(data || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addLook = async () => {
    try {
      await api.post("/admin/style-looks", { label: "New look", product_ids: [] });
      toast.success("Added");
      await load();
    } catch { toast.error("Failed to add"); }
  };

  const patch = async (id, patchBody) => {
    try {
      await api.put(`/admin/style-looks/${id}`, patchBody);
      await load();
    } catch { toast.error("Update failed"); }
  };

  const removeLook = async (id) => {
    if (!window.confirm("Delete this look?")) return;
    try {
      await api.delete(`/admin/style-looks/${id}`);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  const uploadImage = async (id, file) => {
    setBusyId(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/style-looks/${id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image updated");
      await load();
    } catch { toast.error("Upload failed"); }
    finally { setBusyId(null); }
  };

  // Client-side metadata guard: browsers can read <video> metadata quickly, so
  // we bounce anything > 15s OR non-9:16 before sending the megabytes to the server.
  const readVideoMeta = (file) => new Promise((resolve, reject) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.playsInline = true;
    v.onloadedmetadata = () => {
      const meta = { duration: v.duration, width: v.videoWidth, height: v.videoHeight };
      URL.revokeObjectURL(v.src);
      resolve(meta);
    };
    v.onerror = () => { URL.revokeObjectURL(v.src); reject(new Error("Could not read video metadata")); };
    v.src = URL.createObjectURL(file);
  });

  const uploadVideo = async (id, file) => {
    let meta;
    try { meta = await readVideoMeta(file); }
    catch { toast.error("Couldn't read video — try a different file"); return; }
    const { duration, width, height } = meta;
    if (!isFinite(duration) || duration <= 0) { toast.error("Invalid video"); return; }
    if (duration > 15.5) { // small tolerance for float rounding
      toast.error(`Video is ${duration.toFixed(1)}s — please keep it under 15 seconds.`);
      return;
    }
    // Enforce 9:16 portrait aspect ratio (0.5625). Allow ±3% tolerance for
    // encoders that round odd frame dimensions.
    if (!width || !height) { toast.error("Couldn't read video dimensions"); return; }
    const ratio = width / height;
    const expected = 9 / 16;
    if (Math.abs(ratio - expected) / expected > 0.03) {
      toast.error(
        `Video must be 9:16 portrait. Yours is ${width}×${height} (${ratio.toFixed(2)}). Please re-export as 9:16 (e.g. 1080×1920).`,
        { duration: 6000 }
      );
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error(`Video is ${(file.size / 1_000_000).toFixed(1)} MB — max 25 MB.`);
      return;
    }
    setBusyId(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/style-looks/${id}/video`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Video uploaded (${duration.toFixed(1)}s, 9:16)`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusyId(null);
    }
  };

  const toggleProduct = (look, pid) => {
    const current = new Set(look.product_ids || []);
    if (current.has(pid)) current.delete(pid); else current.add(pid);
    patch(look.id, { product_ids: Array.from(current) });
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const order = [...items];
    [order[idx], order[j]] = [order[j], order[idx]];
    try {
      await api.put("/admin/style-looks/reorder", { order: order.map((o) => o.id) });
      await load();
    } catch { toast.error("Reorder failed"); }
  };

  const LOOK_PRESETS = [
    "Casual everyday",
    "College / brunch",
    "Office / formal",
    "Wedding guest",
    "Festive Indian wear",
    "Evening party",
  ];

  return (
    <div data-testid="admin-style-looks-panel">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Manage</div>
          <h1 className="font-display text-3xl font-light">Style Inspiration Gallery</h1>
          <p className="text-sj-muted text-sm mt-2">Upload lifestyle photos, label them, and link each to product(s) featured. Displayed on Home (top 8) and the full /style page.</p>
        </div>
        <button type="button" onClick={addLook} className="btn-primary text-sm" data-testid="admin-look-add"><Plus size={14} /> Add look</button>
      </div>

      {loading ? (
        <StarLoader />
      ) : items.length === 0 ? (
        <div className="text-sj-muted text-sm py-10 text-center border border-dashed border-sj-ink/10 rounded-md">No looks yet. Click "Add look" to get started.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {items.map((o, idx) => (
            <div key={o.id} className="bg-white rounded-md border border-sj-ink/5 overflow-hidden p-4" data-testid={`admin-look-${o.id}`}>
              <div className="grid grid-cols-1 sm:grid-cols-[160px,1fr] gap-4">
                <div>
                  <div className="relative aspect-[9/16] bg-sj-cream rounded overflow-hidden">
                    {o.video ? (
                      <video src={`/api/videos/${o.video}`} className="w-full h-full object-cover bg-black" muted loop playsInline autoPlay />
                    ) : o.image ? (
                      <img src={resolveImage(o.image, { w: 400 })} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-sj-muted">
                        <ImageIcon size={20} />
                        <div className="text-[10px] mt-1">No media</div>
                      </div>
                    )}
                    {o.video && (
                      <div className="absolute top-1.5 left-1.5 text-[9px] tracking-widest uppercase bg-black/70 text-white px-1.5 py-0.5 rounded">
                        Video
                      </div>
                    )}
                  </div>
                  <label className="btn-outline text-xs cursor-pointer w-full justify-center mt-2" data-testid={`admin-look-image-${o.id}`}>
                    <Upload size={12} /> {busyId === o.id ? "Uploading…" : (o.image ? "Replace image" : "Upload image")}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={busyId === o.id}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) setCropTarget({ file: f, id: o.id }); e.target.value = ""; }}
                    />
                  </label>
                  <label className="btn-outline text-xs cursor-pointer w-full justify-center mt-1.5" data-testid={`admin-look-video-${o.id}`}>
                    <Upload size={12} /> {o.video ? "Replace video" : "Upload video (9:16, ≤15s)"}
                    <input
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime"
                      className="hidden"
                      disabled={busyId === o.id}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadVideo(o.id, f); e.target.value = ""; }}
                    />
                  </label>
                </div>

                <div className="space-y-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-sj-muted">Label</label>
                    <input
                      type="text"
                      list={`look-presets-${o.id}`}
                      defaultValue={o.label}
                      onBlur={(e) => e.target.value !== o.label && patch(o.id, { label: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-sj-ink/15 rounded text-sm"
                      data-testid={`admin-look-label-${o.id}`}
                    />
                    <datalist id={`look-presets-${o.id}`}>
                      {LOOK_PRESETS.map((p) => <option key={p} value={p} />)}
                    </datalist>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-sj-muted">Linked products ({(o.product_ids || []).length})</label>
                    <div className="relative mt-1">
                      <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sj-muted" />
                      <input
                        type="text"
                        value={productSearch[o.id] || ""}
                        onChange={(e) => setProductSearch((prev) => ({ ...prev, [o.id]: e.target.value }))}
                        placeholder="Search products…"
                        className="w-full pl-7 pr-2 py-1.5 border border-sj-ink/15 rounded text-xs outline-none focus:border-sj-goldDark"
                        data-testid={`admin-look-product-search-${o.id}`}
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto border border-sj-ink/10 rounded p-2 mt-1 space-y-1">
                      {products.length === 0 ? (
                        <div className="text-xs text-sj-muted">No products available.</div>
                      ) : (() => {
                        const q = (productSearch[o.id] || "").trim().toLowerCase();
                        const filtered = q ? products.filter((p) => (p.name || "").toLowerCase().includes(q)) : products;
                        if (filtered.length === 0) {
                          return <div className="text-xs text-sj-muted py-2">No matches.</div>;
                        }
                        return filtered.map((p) => {
                          const checked = (o.product_ids || []).includes(p.id);
                          return (
                            <label key={p.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-sj-cream/60 px-1 py-0.5 rounded">
                              <input type="checkbox" checked={checked} onChange={() => toggleProduct(o, p.id)} data-testid={`admin-look-product-${o.id}-${p.id}`} />
                              <span className="truncate">{p.name}</span>
                            </label>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="p-2 rounded border border-sj-ink/15 disabled:opacity-40" data-testid={`admin-look-up-${o.id}`}><ArrowUp size={13} /></button>
                    <button type="button" onClick={() => move(idx, +1)} disabled={idx === items.length - 1} className="p-2 rounded border border-sj-ink/15 disabled:opacity-40" data-testid={`admin-look-down-${o.id}`}><ArrowDown size={13} /></button>
                    <div className="flex-1" />
                    <button type="button" onClick={() => removeLook(o.id)} className="p-2 rounded border border-sj-ink/15 hover:border-sj-rose hover:text-sj-rose" data-testid={`admin-look-delete-${o.id}`}><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <CropModal
        file={cropTarget?.file || null}
        title="Crop lifestyle image"
        aspects={[
          { label: "4:5", value: 4 / 5 },
          { label: "3:4", value: 3 / 4 },
          { label: "1:1", value: 1 },
          { label: "Free", value: null },
        ]}
        onCancel={() => setCropTarget(null)}
        onSave={async (cropped) => {
          const { id } = cropTarget;
          setCropTarget(null);
          await uploadImage(id, cropped);
        }}
      />
    </div>
  );
}


/* -------------------- Exchange Requests Panel -------------------- */
function ExchangeRequestsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/exchange-requests");
      setItems(data || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/exchange-requests/${id}`, { status });
      toast.success("Updated");
      await load();
    } catch { toast.error("Update failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try { await api.delete(`/admin/exchange-requests/${id}`); await load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-exchange-panel">
      <div className="mb-6">
        <div className="overline">Support</div>
        <h1 className="font-display text-3xl font-light">Exchange Requests</h1>
        <p className="text-sm text-sj-muted mt-2">Requests captured by Moon when customers report damaged or defective items. Review each one and approve or deny yourself — Moon never decides.</p>
      </div>

      {loading ? (
        <StarLoader />
      ) : items.length === 0 ? (
        <div className="text-sj-muted text-sm py-10 text-center border border-dashed border-sj-ink/10 rounded-md">No exchange requests yet.</div>
      ) : (
        <div className="space-y-4">
          {items.map((r) => (
            <div key={r.id} className="bg-white rounded-md border border-sj-ink/5 p-5" data-testid={`admin-exchange-${r.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-sj-muted">{new Date(r.created_at).toLocaleString()}</div>
                  <div className="mt-1 text-sm">
                    <span className="text-sj-muted">Order ref: </span>
                    <span className="font-medium">{r.order_ref || r.email || "—"}</span>
                  </div>
                  {r.customer_name && <div className="text-sm text-sj-muted">Customer: {r.customer_name}</div>}
                  <div className="mt-3 p-3 rounded-md bg-sj-cream text-sm leading-relaxed" data-testid={`admin-exchange-desc-${r.id}`}>{r.description}</div>
                  {r.photos?.length > 0 && (
                    <div className="mt-3 flex gap-2 flex-wrap">
                      {r.photos.map((p, i) => (
                        <a key={i} href={resolveImage(p)} target="_blank" rel="noreferrer" className="block">
                          <img src={resolveImage(p, { w: 200 })} alt="" className="h-20 w-20 object-cover rounded border border-sj-ink/10" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 min-w-[180px]">
                  <span className={`text-[11px] px-2.5 py-1 rounded-full uppercase tracking-wider ${
                    r.status === "approved"    ? "bg-green-100 text-green-700" :
                    r.status === "denied"      ? "bg-red-100 text-red-700" :
                    r.status === "in_review"   ? "bg-blue-100 text-blue-700" :
                    r.status === "followed_up" ? "bg-purple-100 text-purple-700" :
                    "bg-amber-100 text-amber-700"
                  }`} data-testid={`admin-exchange-status-${r.id}`}>{r.status.replace(/_/g, " ")}</span>
                  <select
                    value={r.status}
                    onChange={(e) => setStatus(r.id, e.target.value)}
                    className="text-xs px-3 py-2 border border-sj-ink/15 rounded-md bg-white w-full"
                    data-testid={`admin-exchange-select-${r.id}`}
                  >
                    <option value="new">New</option>
                    <option value="in_review">In review</option>
                    <option value="approved">Approve</option>
                    <option value="denied">Deny</option>
                    <option value="followed_up">Followed up</option>
                  </select>
                  <button type="button" onClick={() => del(r.id)} className="text-xs text-sj-rose hover:underline" data-testid={`admin-exchange-delete-${r.id}`}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function SettingsPanel() {
  const [pct, setPct] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      setPct(String(data.bundle_discount_pct ?? 25));
    }).catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    const val = Number(pct);
    if (!Number.isFinite(val) || val < 0 || val > 90) {
      toast.error("Enter a value between 0 and 90");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/settings", { bundle_discount_pct: val });
      toast.success(`Bundle discount set to ${val}%`);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings-panel">
      <div className="mb-6">
        <div className="overline">Site controls</div>
        <h2 className="font-display text-3xl font-light mt-2">Settings</h2>
        <p className="text-sm text-sj-inkSoft mt-2 max-w-lg">Tune site-wide behaviour like Moon's Pairings bundle discount for festival sales and campaigns.</p>
      </div>

      <div className="bg-white rounded-md border border-sj-ink/10 p-6 sm:p-8 max-w-xl">
        <div className="flex items-start gap-3">
          <Sparkles size={18} className="text-sj-goldDark mt-1" />
          <div className="flex-1">
            <h3 className="font-display text-lg text-sj-ink">Moon's Pairings bundle discount</h3>
            <p className="text-xs text-sj-inkSoft mt-1">
              % off applied on the Product Detail page when a customer buys the current product together with Moon's paired items.
            </p>
          </div>
        </div>

        <div className="mt-6">
          <label className="text-xs text-sj-inkSoft" htmlFor="bundle-pct">Discount %</label>
          <div className="mt-1.5 flex items-center gap-3">
            <div className="relative">
              <input
                id="bundle-pct"
                type="number"
                min="0"
                max="90"
                step="1"
                value={loading ? "" : pct}
                onChange={(e) => setPct(e.target.value)}
                className="w-32 bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md pl-4 pr-8 h-11 text-base"
                data-testid="settings-bundle-pct-input"
                disabled={loading}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sj-inkSoft text-sm">%</span>
            </div>
            <button
              type="button"
              onClick={save}
              disabled={saving || loading}
              className="btn-primary text-sm disabled:opacity-60"
              data-testid="settings-bundle-pct-save"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
          <p className="mt-3 text-[11px] text-sj-muted">Allowed range: 0–90%. Reflects on all product pages instantly.</p>
        </div>
      </div>
    </div>
  );
}

function ColorSwatchesPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", hex: "#" });
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/color-swatches").then(({ data }) => setRows(data || [])).catch(() => setRows([]));
  };

  useEffect(() => { load(); }, []);

  const startEdit = (r) => { setEditingId(r.id); setForm({ name: r.name, hex: r.hex }); };
  const resetForm = () => { setEditingId(null); setForm({ name: "", hex: "#" }); };

  const save = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const hex = form.hex.trim();
    if (!name) return toast.error("Name is required");
    if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) return toast.error("Hex must be #RGB or #RRGGBB");
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/admin/color-swatches/${editingId}`, { name, hex });
        toast.success("Swatch updated");
      } else {
        await api.post("/admin/color-swatches", { name, hex });
        toast.success("Swatch added");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete swatch "${r.name}"?`)) return;
    try {
      await api.delete(`/admin/color-swatches/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="max-w-3xl" data-testid="color-swatches-panel">
      <div className="overline">Color Swatches</div>
      <h1 className="font-display text-3xl mt-1">Manage color hex codes</h1>
      <p className="text-sm text-sj-muted mt-1">
        Add a color name (e.g. "Mint Green") and its exact hex code (e.g. #98D8C8).
        Whenever this color name appears on a product, the swatch circle across the site
        will render in this exact color.
      </p>

      <form onSubmit={save} className="mt-6 flex flex-wrap gap-3 items-end p-4 rounded-md bg-white border border-sj-ink/10" data-testid="color-swatch-form">
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs text-sj-inkSoft">Color name</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Mint Green"
            className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm"
            data-testid="color-swatch-name"
          />
        </div>
        <div className="w-[180px]">
          <label className="text-xs text-sj-inkSoft">Hex code</label>
          <div className="mt-1.5 flex items-center gap-2">
            <input
              value={form.hex}
              onChange={(e) => setForm({ ...form, hex: e.target.value })}
              placeholder="#98D8C8"
              className="flex-1 bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm font-mono"
              data-testid="color-swatch-hex"
            />
            <input
              type="color"
              value={/^#([0-9a-fA-F]{6})$/.test(form.hex) ? form.hex : "#000000"}
              onChange={(e) => setForm({ ...form, hex: e.target.value.toUpperCase() })}
              className="h-11 w-11 rounded-md border border-sj-ink/15 cursor-pointer p-0"
              aria-label="Color picker"
              data-testid="color-swatch-picker"
            />
          </div>
        </div>
        <button type="submit" disabled={busy} className="btn-primary text-sm disabled:opacity-60" data-testid="color-swatch-save">
          {busy ? "Saving…" : editingId ? "Update" : "Add"}
        </button>
        {editingId && (
          <button type="button" onClick={resetForm} className="btn-outline text-sm" data-testid="color-swatch-cancel">
            Cancel
          </button>
        )}
      </form>

      <div className="mt-6" data-testid="color-swatch-list">
        {rows.length === 0 ? (
          <div className="text-sm text-sj-muted p-4 rounded-md bg-white border border-dashed border-sj-ink/15">
            No custom swatches yet. Add one above.
          </div>
        ) : (
          <ul className="divide-y divide-sj-ink/10 rounded-md bg-white border border-sj-ink/10">
            {rows.map((r) => (
              <li key={r.id} className="flex items-center gap-4 p-3.5" data-testid={`swatch-row-${r.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <span
                  className="h-9 w-9 rounded-full border border-sj-ink/15 shrink-0"
                  style={{ background: r.hex }}
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-sj-ink truncate">{r.name}</div>
                  <div className="text-[11px] text-sj-muted font-mono">{r.hex}</div>
                </div>
                <button
                  type="button"
                  onClick={() => startEdit(r)}
                  className="text-xs text-sj-inkSoft hover:text-sj-goldDark"
                  data-testid={`swatch-edit-${r.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => remove(r)}
                  className="text-xs text-sj-rose hover:opacity-80"
                  data-testid={`swatch-delete-${r.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ReviewsPanel() {
  const emptyRow = { rating: 5, text: "", customer_name: "", product_name: "", location: "" };
  const [rows, setRows] = useState([]);
  const [average, setAverage] = useState(0);
  const [count, setCount] = useState(0);
  const [form, setForm] = useState(emptyRow);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/reviews");
      setRows(data.reviews || []);
      setAverage(data.average || 0);
      setCount(data.count || 0);
    } catch { toast.error("Failed to load reviews"); }
  };

  useEffect(() => { load(); }, []);

  const reset = () => { setForm(emptyRow); setEditingId(null); };

  const submit = async () => {
    if (!form.text.trim() || !form.customer_name.trim() || !form.product_name.trim() || !form.location.trim()) {
      return toast.error("Please fill in every field");
    }
    if (form.rating < 1 || form.rating > 5) return toast.error("Rating must be 1–5");
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/admin/reviews/${editingId}`, form);
        toast.success("Review updated");
      } else {
        await api.post("/admin/reviews", form);
        toast.success("Review added");
      }
      reset();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      rating: r.rating,
      text: r.text,
      customer_name: r.customer_name,
      product_name: r.product_name,
      location: r.location,
    });
  };

  const remove = async (r) => {
    if (!confirm(`Delete review by ${r.customer_name}?`)) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Review deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-reviews-panel">
      <div className="mb-6 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="overline">Customer voice</div>
          <h2 className="font-display text-3xl font-light mt-2">Reviews & Testimonials</h2>
          <p className="text-sm text-sj-inkSoft mt-2 max-w-lg">Add real customer reviews here. The homepage "What They're Saying" section and the average rating badge update automatically.</p>
        </div>
        <div className="text-right">
          <div className="font-display text-4xl text-sj-goldDark leading-none">{average.toFixed(1)}</div>
          <div className="mt-1 text-[10px] tracking-[0.2em] uppercase text-sj-inkSoft" data-testid="reviews-admin-average">Avg from {count} reviews</div>
        </div>
      </div>

      {/* Editor form */}
      <div className="bg-white rounded-md border border-sj-ink/10 p-5 sm:p-6 mb-8">
        <div className="text-sm font-medium text-sj-ink mb-4">{editingId ? "Edit review" : "Add a new review"}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-sj-inkSoft">Star rating</label>
            <div className="mt-1.5 flex items-center gap-1" data-testid="review-rating-picker">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, rating: n }))}
                  className="p-1"
                  aria-label={`Set rating to ${n}`}
                  data-testid={`review-star-${n}`}
                >
                  <Star size={20} strokeWidth={1.5} className={n <= form.rating ? "fill-sj-goldDark text-sj-goldDark" : "text-sj-goldDark/30"} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-sj-inkSoft">Customer name (e.g. "Ananya S.")</label>
            <input type="text" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm" data-testid="review-customer-name" />
          </div>
          <div>
            <label className="text-xs text-sj-inkSoft">Product name</label>
            <input type="text" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm" placeholder="e.g. Diamond Dew Necklace Set" data-testid="review-product-name" />
          </div>
          <div>
            <label className="text-xs text-sj-inkSoft">City / location</label>
            <input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm" placeholder="e.g. Mumbai" data-testid="review-location" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-sj-inkSoft">Review text</label>
            <textarea rows={3} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} maxLength={600} className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 py-3 text-sm leading-relaxed resize-y" data-testid="review-text" />
            <div className="mt-1 text-[10px] text-sj-muted text-right">{form.text.length}/600</div>
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <button type="button" onClick={submit} disabled={busy} className="btn-primary text-sm disabled:opacity-60" data-testid="review-save">
            {busy ? "Saving…" : editingId ? "Save changes" : "Add review"}
          </button>
          {editingId && (
            <button type="button" onClick={reset} className="text-sm text-sj-inkSoft hover:text-sj-ink" data-testid="review-cancel-edit">Cancel</button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="space-y-3">
        {rows.length === 0 && (
          <div className="p-6 rounded-md bg-white border border-dashed border-sj-ink/15 text-sm text-sj-inkSoft text-center">
            No reviews yet. Add your first one above.
          </div>
        )}
        {rows.map((r) => (
          <div key={r.id} className="bg-white rounded-md border border-sj-ink/10 p-4 sm:p-5 flex items-start justify-between gap-4" data-testid={`review-row-${r.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} size={13} strokeWidth={1.5} className={n <= r.rating ? "fill-sj-goldDark text-sj-goldDark" : "text-sj-goldDark/25"} />
                ))}
                <span className="text-xs text-sj-inkSoft ml-1">{r.customer_name} · {r.location}</span>
              </div>
              <div className="mt-2 text-sm text-sj-ink line-clamp-3">{r.text}</div>
              <div className="mt-2 text-[11px] uppercase tracking-wider text-sj-inkSoft">{r.product_name}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button type="button" onClick={() => startEdit(r)} className="p-2 rounded hover:bg-sj-cream" aria-label="Edit review" data-testid={`review-edit-${r.id}`}>
                <Pencil size={14} className="text-sj-ink" />
              </button>
              <button type="button" onClick={() => remove(r)} className="p-2 rounded hover:bg-sj-rose/10" aria-label="Delete review" data-testid={`review-delete-${r.id}`}>
                <Trash2 size={14} className="text-sj-rose" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function BlogPanel() {
  const emptyRow = { title: "", excerpt: "", content: "", cover_image: "", tags: "", published: true };
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(emptyRow);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [coverFile, setCoverFile] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data);
    } catch { toast.error("Failed to load posts"); }
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(emptyRow); setEditingId(null); setCoverFile(null); };

  const uploadCover = async (postId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    // Reuse site-image upload endpoint — it just stores the raw file.
    const { data } = await api.post("/admin/site-images/blog-cover", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.path;
  };

  const submit = async () => {
    if (!form.title.trim() || !form.content.trim()) return toast.error("Title and content are required");
    setBusy(true);
    try {
      let coverPath = form.cover_image;
      if (coverFile) {
        try { coverPath = await uploadCover(editingId || "new", coverFile); }
        catch { toast.error("Cover upload failed"); }
      }
      const payload = {
        title: form.title.trim(),
        excerpt: form.excerpt.trim(),
        content: form.content,
        cover_image: coverPath || null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        published: !!form.published,
      };
      if (editingId) {
        await api.put(`/admin/blog/${editingId}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog", payload);
        toast.success("Post published");
      }
      reset();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title,
      excerpt: p.excerpt || "",
      content: p.content || "",
      cover_image: p.cover_image || "",
      tags: (p.tags || []).join(", "),
      published: p.published,
    });
    setCoverFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (p) => {
    if (!confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/blog/${p.id}`);
      toast.success("Post deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-blog-panel">
      <div className="mb-6">
        <div className="overline">Journal</div>
        <h2 className="font-display text-3xl font-light mt-2">Blog</h2>
        <p className="text-sm text-sj-inkSoft mt-2 max-w-lg">Write style guides, gift edits and jewellery care notes. Fresh content is one of the biggest SEO ranking signals.</p>
      </div>

      <div className="bg-white rounded-md border border-sj-ink/10 p-5 sm:p-6 mb-8">
        <div className="text-sm font-medium text-sj-ink mb-4">{editingId ? "Edit post" : "New post"}</div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="text-xs text-sj-inkSoft">Title</label>
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. How to Style Layered Necklaces" className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm" data-testid="blog-title-input" />
          </div>
          <div>
            <label className="text-xs text-sj-inkSoft">Excerpt (shown on the /blog list)</label>
            <input type="text" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="One-line summary" maxLength={300} className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm" data-testid="blog-excerpt-input" />
          </div>
          <div>
            <label className="text-xs text-sj-inkSoft">Content (paragraphs are separated by a blank line)</label>
            <textarea rows={12} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 py-3 text-sm leading-relaxed font-mono resize-y" data-testid="blog-content-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-sj-inkSoft">Cover image</label>
              <input type="file" accept="image/*" onChange={(e) => setCoverFile(e.target.files?.[0] || null)} className="mt-1.5 w-full text-sm" data-testid="blog-cover-input" />
              {form.cover_image && !coverFile && (
                <div className="mt-2 text-[11px] text-sj-muted">Current: {form.cover_image.split("/").pop()}</div>
              )}
            </div>
            <div>
              <label className="text-xs text-sj-inkSoft">Tags (comma-separated)</label>
              <input type="text" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="styling, gifts, care" className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm" data-testid="blog-tags-input" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-sj-ink">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="blog-published-input" />
            <span>Published (visible on /blog)</span>
          </label>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <button type="button" onClick={submit} disabled={busy} className="btn-primary text-sm disabled:opacity-60" data-testid="blog-save">
            {busy ? "Saving…" : editingId ? "Save changes" : "Publish post"}
          </button>
          {editingId && <button type="button" onClick={reset} className="text-sm text-sj-inkSoft hover:text-sj-ink" data-testid="blog-cancel">Cancel</button>}
        </div>
      </div>

      <div className="space-y-3">
        {posts.length === 0 && (
          <div className="p-6 rounded-md bg-white border border-dashed border-sj-ink/15 text-sm text-sj-inkSoft text-center">No posts yet.</div>
        )}
        {posts.map((p) => (
          <div key={p.id} className="bg-white rounded-md border border-sj-ink/10 p-4 sm:p-5 flex items-start justify-between gap-4" data-testid={`blog-row-${p.slug}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] tracking-[0.14em] uppercase px-2 py-0.5 rounded-full ${p.published ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{p.published ? "Live" : "Draft"}</span>
                <span className="text-xs text-sj-muted">/blog/{p.slug}</span>
              </div>
              <div className="mt-2 font-medium text-sj-ink">{p.title}</div>
              {p.excerpt && <div className="mt-1 text-sm text-sj-inkSoft line-clamp-2">{p.excerpt}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button type="button" onClick={() => startEdit(p)} className="p-2 rounded hover:bg-sj-cream" aria-label="Edit" data-testid={`blog-edit-${p.slug}`}><Pencil size={14} className="text-sj-ink" /></button>
              <button type="button" onClick={() => remove(p)} className="p-2 rounded hover:bg-sj-rose/10" aria-label="Delete" data-testid={`blog-delete-${p.slug}`}><Trash2 size={14} className="text-sj-rose" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

